var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import React, { useMemo, useRef } from 'react';
import { WidgetType } from '@common/api';
import { useLanguage } from '@base/LanguageProvider';
import WidgetLoader from '@main/dashboard/WidgetLoader';
import WidgetMenu from '@main/dashboard/WidgetMenu';
import WidgetParentChangeModal from '@main/dashboard/WidgetParentChangeModal';
import VChart from '@shared/chart/VChart';
import Manager from '@shared/modal/Manager';
import { getTextWidth } from '@shared/chart/util';
import { Copy, Delete, Edit, Select } from '@shared/icons/icons';
import useChartData from '@data/hooks/chartData';
import { parseWidgetConfig } from '@data/widgets/widgetConfig';
import { useCloneWidget, useDeleteWidget } from '@data/widgets/widgetActions';
import useDebouncedResizeObserver from '@hooks/useDebouncedResizeObserver';
import { Chart as ChartType } from '@util/enums';
import { ChartContent, WidgetBox, WidgetContent, WidgetTitle } from '@styled/Widget';
import { appColors } from '@styled/useTheme';
import ChartEditor from './ChartEditor';
import { useAuth } from '../../../providers/AuthProvider';
function Legend(_a) {
    var legendLabels = _a.legendLabels, legendHeight = _a.legendHeight, largestLabel = _a.largestLabel, containerWidth = _a.containerWidth;
    return (React.createElement("svg", { role: "presentation", height: "".concat(legendHeight, "px"), width: containerWidth },
        React.createElement("g", { transform: "translate(".concat(containerWidth > largestLabel ? Math.round((containerWidth - largestLabel) / 2) : 0, ", 0)"), width: containerWidth, style: { transformOrigin: '0px 0px 0px' } }, legendLabels.map(function (l, idx) {
            return (React.createElement("g", { key: idx },
                React.createElement("circle", { cy: l.labelY, cx: l.labelX + 10, r: "6px", fill: l.color }),
                React.createElement("text", { y: "0", dy: l.labelY + 5, x: l.labelX, fill: appColors.ui.dark },
                    React.createElement("tspan", { key: l.id, dx: 20 }, l.title))));
        }))));
}
function Chart(_a) {
    var gridId = _a.gridId, widget = _a.widget, documentActions = _a.documentActions, tagSlicersData = _a.tagSlicersData;
    var user = useAuth().user;
    var wrapperRef = useRef(null);
    var _b = useLanguage(), L = _b.L, excelLocalizedNumberFormat = _b.excelLocalizedNumberFormat;
    var deleteWidget = useDeleteWidget();
    var cloneWidget = useCloneWidget();
    var widgetConfig = useMemo(function () { return parseWidgetConfig(widget.config, WidgetType.CHART); }, [widget.config]);
    var chartData = useChartData(widget.id, widgetConfig, tagSlicersData.activeTagIds);
    var _c = useDebouncedResizeObserver(500), widgetRef = _c.ref, width = _c.width, height = _c.height;
    var y = 10;
    var largestLabel = 0;
    var x = 0;
    var legendHeight = y + 10;
    var legendLabels = widgetConfig.lines.map(function (line) {
        var l = __assign(__assign({}, line), { labelX: x, labelY: y, labelWidth: Math.round(getTextWidth(line.title, '15px', 'Open Sans')) + 25 });
        x += l.labelWidth;
        if (x >= width) {
            l.labelX = 0;
            l.labelY += 20;
            x = Math.round(getTextWidth(l.title, '15px', 'Open Sans')) + 25;
            y += 20;
        }
        var current = l.labelX + l.labelWidth;
        if (current > largestLabel) {
            largestLabel = current;
        }
        return l;
    });
    var widgetMenuItems = [
        {
            icon: Edit,
            actionName: L('Edit Tile'),
            isDisabled: !(documentActions === null || documentActions === void 0 ? void 0 : documentActions.canUpdate),
            action: function () { return Manager.renderModal(React.createElement(ChartEditor, { widget: widget, gridId: gridId })); },
        },
        {
            icon: Copy,
            actionName: L('Duplicate'),
            isDisabled: !(user === null || user === void 0 ? void 0 : user.classActions.documentClassActions.canDuplicate),
            action: function () { return cloneWidget.mutate(widget); },
        },
        {
            icon: Select,
            actionName: L('Move to'),
            isDisabled: !(documentActions === null || documentActions === void 0 ? void 0 : documentActions.canUpdate),
            action: function () { return Manager.renderModal(React.createElement(WidgetParentChangeModal, { widget: widget, gridId: gridId })); },
        },
        { spacer: true },
        {
            icon: Delete,
            actionName: L('Delete Tile'),
            isDisabled: !(documentActions === null || documentActions === void 0 ? void 0 : documentActions.canDelete),
            action: function () { return deleteWidget.mutate(widget.id); },
            destructive: true,
        },
    ];
    return (React.createElement(React.Fragment, null,
        React.createElement(WidgetBox, { ref: wrapperRef },
            widget.title && widget.title != '' && React.createElement(WidgetTitle, null, widget.title),
            React.createElement(WidgetMenu, { wrapperRef: wrapperRef, menuItems: widgetMenuItems }),
            React.createElement(WidgetContent, { "$overflow": "hidden" },
                React.createElement(ChartContent, { id: "pin-chart:".concat(widget.id), ref: widgetRef },
                    chartData && width && height ? (React.createElement("div", { style: { position: 'relative' } },
                        React.createElement(VChart, { formatter: excelLocalizedNumberFormat, pie: widgetConfig.chartType === ChartType.PIE, showLabels: widgetConfig.showLabels, hideAxis: widgetConfig.hideAxis, labels: widgetConfig.labels, horizontal: widgetConfig.chartHorizontal, wId: widget.id, containerWidth: width, containerHeight: height - legendHeight, data: chartData }))) : null,
                    legendLabels.length > 0 && (React.createElement("div", { id: "pin-chart:".concat(widget.id, "-legend") },
                        React.createElement(Legend, { legendLabels: legendLabels, legendHeight: legendHeight, largestLabel: largestLabel, containerWidth: width })))))),
        React.createElement(WidgetLoader, { wId: widget.id })));
}
export default Chart;
